
/**
 * Builds an aggregation pipeline to fetch Job Order employees with populated relations
 * @param {Object} options - Pipeline options (currently unused)
 * @returns {Array} MongoDB aggregation pipeline
 */
export function _getJobOrderEmployeesPipeline(options = {}) {
  return [
    {
      $lookup: {
        from: "employment_type",
        localField: "employmentType.id",
        foreignField: "_id",
        as: "employmentTypeLookup"
      }
    },
    {
      $lookup: {
        from: "non_plantilla",
        localField: "nonRegPosition.id",
        foreignField: "_id",
        as: "nonRegPositionLookup"
      }
    },
    {
      $lookup: {
        from: "schedule_template",
        localField: "selectedSchedule.id",
        foreignField: "_id",
        as: "selectedScheduleLookup"
      }
    },
    {
      $unwind: {
        path: "$employmentTypeLookup",
        preserveNullAndEmptyArrays: true
      }
    },
    {
      $unwind: {
        path: "$nonRegPositionLookup",
        preserveNullAndEmptyArrays: true
      }
    },
    {
      $unwind: {
        path: "$selectedScheduleLookup",
        preserveNullAndEmptyArrays: true
      }
    },
    {
      $match: {
        "employmentTypeLookup.name": "Job Order"
      }
    },
    {
      $addFields: {
        employmentType: {
          $cond: {
            if: { $ne: ["$employmentTypeLookup", null] },
            then: {
              $mergeObjects: [
                "$employmentTypeLookup",
                { id: "$employmentTypeLookup._id" }
              ]
            },
            else: "$employmentType"
          }
        },
        nonRegPosition: {
          $cond: {
            if: { $ne: ["$nonRegPositionLookup", null] },
            then: {
              $mergeObjects: [
                "$nonRegPositionLookup",
                { id: "$nonRegPositionLookup._id" }
              ]
            },
            else: "$nonRegPosition"
          }
        },
        selectedSchedule: {
          $cond: {
            if: { $ne: ["$selectedScheduleLookup", null] },
            then: {
              $mergeObjects: [
                "$selectedScheduleLookup",
                { id: "$selectedScheduleLookup._id" }
              ]
            },
            else: "$selectedSchedule"
          }
        }
      }
    },
    {
      $sort: { surname: 1 }
    },
    {
      $project: {
        id: "$_id",
        biometricId: 1,
        Firstname: 1,
        Middlename: 1,
        surname: 1,
        accountNumber: 1,
        agency: 1,
        allowance: 1,
        email: 1,
        employmentType: 1,
        gsis: 1,
        isGraduated: 1,
        isSubmitted: 1,
        nonRegPosition: 1,
        payrollRelation: 1,
        percentTag: 1,
        selectedSchedule: 1,
        supervisor: 1,
        section: 1,
        twentyFourSeven: 1
      }
    }
  ];
}

/**
 * Special Payroll: lean aggregate (no relation lookups).
 *
 * Audited against FormPayrollPage / FormPayrollPresenter / CalculationPayroll
 * (13th & 14th cases) + FormPresenterHelper commonProp / TableHeaderSP:
 *
 * REQUIRED
 * - id, agency                         → 13th net (CSV by agency); row key
 * - Firstname, Middlename, surname     → table / print identity
 * - accountNumber                      → table ACCT NO.
 * - earnedForThePeriod, isGraduated    → 14th (20% premium) net
 *
 * KEEP (shared plumbing, not SP net math)
 * - biometricId → commonProp + attendance fallback (id used if missing)
 *
 * NOT NEEDED for SP
 * - employmentType, nonRegPosition, selectedSchedule,
 *   selected_schedule_assignment, salaryGrade, salaryCode, email,
 *   dateOfSuspension, section, office, twentyFourSeven, …
 */
export function _getSpecialPayrollEmployeesPipeline(options = {}) {
  const { where = {}, sort = { Firstname: 1, Middlename: 1, surname: 1 } } =
    options;
  const pipeline = [];

  if (where && Object.keys(where).length > 0) {
    pipeline.push({ $match: where });
  }

  if (sort && Object.keys(sort).length > 0) {
    pipeline.push({ $sort: sort });
  }

  pipeline.push({
    $project: {
      _id: 0,
      id: "$_id",
      biometricId: 1,
      Firstname: 1,
      Middlename: 1,
      surname: 1,
      accountNumber: 1,
      agency: 1,
      isGraduated: 1,
      earnedForThePeriod: 1,
    },
  });

  return pipeline;
}

function _getAirportAssignPipeline(options = {}) {
  const { employeeIds = null } = options;
  const pipeline = [
    {
      $unwind: {
        path: "$assignRelation",
        preserveNullAndEmptyArrays: true
      }
    },
    {
      $lookup: {
        from: "airport_assign_relation",
        localField: "assignRelation.id",
        foreignField: "_id",
        as: "airportAssignLookup",
        pipeline: employeeIds && employeeIds.length > 0 ? [
          {
            $match: {
              "employee.id": { $in: employeeIds }
            }
          }
        ] : []
      }
    },
    {
      $unwind: {
        path: "$airportAssignLookup",
        preserveNullAndEmptyArrays: true
      }
    },
    {
      $lookup: {
        from: "employees",
        localField: "airportAssignLookup.employee.id",
        foreignField: "_id",
        as: "employeeLookup"
      }
    },
    {
      $unwind: {
        path: "$employeeLookup",
        preserveNullAndEmptyArrays: true
      }
    },
    {
      $addFields: {
        assignRelation: {
          $cond: {
            if: { $ne: ["$airportAssignLookup", null] },
            then: {
              $mergeObjects: [
                // "$airportAssignLookup",
                { 
                  id: {
                    $ifNull: ["$airportAssignLookup.id", "$airportAssignLookup._id"]
                  }
                },
                {
                  employee: {
                    $cond: {
                      if: { $ne: ["$employeeLookup", null] },
                      then: {
                        id: "$employeeLookup._id"
                      },
                      else: "$assignRelation.employee"
                    }
                  }
                }
              ]
            },
            else: "$assignRelation"
          }
        }
      }
    },
    {
      $group: {
        _id: "$_id",
        id: { $first: "$_id" },
        startDate: { $first: "$startDate" },
        endDate: { $first: "$endDate" },
        assignRelation: { $push: "$assignRelation" }
      }
    },
    {
      $project: {
        _id: 0,
        id: 1,
        startDate: 1,
        endDate: 1,
        assignRelation: 1
      }
    }
  ];

  return pipeline;
}

function _getDailyTimeRecordPipeline(options = {}) {
  const { employeeIds = null, dateRange = null } = options;
  const pipeline = [];
  
  if (employeeIds && employeeIds.length > 0) {
    pipeline.push({
      $match: {
        "employee.id": { $in: employeeIds }
      }
    });
  }

  if (dateRange && dateRange.startDate && dateRange.endDate) {
    pipeline.push({
      $match: {
        $expr: {
          $and: [
            {
              $gte: [
                "$date",
                { $dateFromString: { dateString: dateRange.startDate } }
              ]
            },
            {
              $lt: [
                "$date",
                { $dateFromString: { dateString: dateRange.endDate } }
              ]
            }
          ]
        }
      }
    });
  }

  pipeline.push({
    $project: {
      _id: 0,
      id: "$_id",
      date: 1,
      employee: {
        id: "$employee.id"
      },
      schedule: {
        scheduleArray: "$schedule.scheduleArray",
        selectedOption: "$schedule.selectedOption",
        startingDate: "$schedule.startingDate"
      },
      timeAmOut: 1,
      timeIn: 1,
      timeOut: 1,
      timePmIn: 1,
      timeRecStats: 1,
      twentyFourSeven: 1,
    }
  });

  return pipeline;
}


function _getLeavePipeline(options = {}) {
  const { employeeIds = null, dateRange = null, categoryType = "With Pay" } = options;
  const pipeline = [];

  const matchConditions = {
    stats: "Approved",
    categoryType: categoryType
  };

  if (employeeIds && employeeIds.length > 0) {
    matchConditions["employee.id"] = { $in: employeeIds };
  }

  pipeline.push({
    $match: matchConditions
  });

  if (dateRange && dateRange.startDate && dateRange.endDate) {
    pipeline.push({
      $match: {
        $expr: {
          $and: [
            {
              $gte: [
                "$startdate",
                { $dateFromString: { dateString: dateRange.startDate } }
              ]
            },
            {
              $lt: [
                "$enddate",
                { $dateFromString: { dateString: dateRange.endDate } }
              ]
            }
          ]
        }
      }
    });
  }

  pipeline.push({
    $project: {
      _id: 0,
      id: "$_id",
      startdate: 1,
      enddate: 1,
      employee: {
        id: "$employee.id"
      },
      selectedType: 1,
      stats: 1,
      categoryType: 1,
    }
  });

  return pipeline;
}

function _getManualDtrPipeline(options = {}) {
  const { employeeIds = null, dateStrings = null } = options;
  const matchConditions = {};
  
  if (employeeIds?.length > 0) {
    matchConditions["employee.id"] = { $in: employeeIds };
  }
  
  if (dateStrings?.length > 0) {
    matchConditions["selectDate"] = {
      $elemMatch: {
        $elemMatch: { $in: dateStrings }
      }
    };
  }
  
  
  const pipeline = [];
  
  if (Object.keys(matchConditions).length > 0) {
    pipeline.push({ $match: matchConditions });
  }
  
  pipeline.push({
    $project: {
      _id: 0,
      id: "$_id",
      selectDate: 1,
      employee: {
        id: "$employee.id"
      }
    }
  });
  
  return pipeline;
}

function _getHolidaysPipeline(options = {}) {
  const { dateRange = null } = options;

  const andConditions = [
    { $in: ["*", "$acl.read"] },
    {
      $or: [
        { $eq: ["$type", null] },
        { $ne: ["$type", "Work Suspension"] }
      ]
    }
  ];

  if (dateRange?.startDate && dateRange?.endDate) {
    andConditions.push({
      $gte: ["$date", { $dateFromString: { dateString: dateRange.startDate } }]
    });
    andConditions.push({
      $lt: ["$date", { $dateFromString: { dateString: dateRange.endDate } }]
    });
  }

  return [
    { $match: { $expr: { $and: andConditions } } },
    {
      $project: {
        _id: 0,
        id: "$_id",
        date: 1,
        name: 1,
        regions: 1,
        offices: 1,
        divisions: 1,
        sections: 1,
        units: 1,
        plantilla: 1
      }
    }
  ];
}

function _getSuspensionsPipeline(options = {}) {
  const { dateRange = null } = options;

  const andConditions = [];

  if (dateRange?.startDate && dateRange?.endDate) {
    andConditions.push({
      $gte: ["$from", { $dateFromString: { dateString: dateRange.startDate } }]
    });
    andConditions.push({
      $lte: ["$to", { $dateFromString: { dateString: dateRange.endDate } }]
    });
  }

  return [
    { $match: { $expr: { $and: andConditions } } },
    {
      $project: {
        _id: 0,
        id: "$_id",
        dateFrom: 1,
        dateTo: 1,
        regions: 1,
        offices: 1,
        divisions: 1,
        sections: 1,
        units: 1,
        plantilla: 1
      }
    }
  ];
}


const pipelineMap = {
  airportPipeline: _getAirportAssignPipeline,
  jobOrderEmployeesPipeline: _getJobOrderEmployeesPipeline,
  specialPayrollEmployeesPipeline: _getSpecialPayrollEmployeesPipeline,
  dailyTimeRecordPipeline: _getDailyTimeRecordPipeline,
  leaveWithPayPipeline: _getLeavePipeline,
  leaveWithoutPayPipeline: _getLeavePipeline,
  manualDtrPipeline: _getManualDtrPipeline,
  holidaysPipeline: _getHolidaysPipeline,
  suspensionsPipeline: _getSuspensionsPipeline,
};

export function getPipelineByKey(key, options = {}) {
  const pipelineFn = pipelineMap[key];
  if (!pipelineFn) {
    return [];
  }
  
  if (key === "leaveWithoutPayPipeline") {
    return pipelineFn({ ...options, categoryType: "Without Pay" });
  }
  
  return pipelineFn(options);
}
